type Toast = HTMLElement;

type ToastLevel = "warning" | "error" | "success";

interface ToastContent {
    title: string;
    body: string;
    level: ToastLevel;
}

export enum ToastDuration {
    Permanent = 0,
    Short = 5000,
    Long = 10000
}

/**
 * The options available for creating a new toast
 */
interface ToastOptions extends ToastContent {
    /**
     * The amount of time to display the toast for. Omit for a permanent toast
     */
    displayDuration?: ToastDuration;
}

/**
 * Create and display a new custom toast
 *
 * @param options The options to create the toast with
 * @param toastParent The parent element that should contain the toast. This should be the same for all toast messages
 */
export const showToast = (
    options: ToastOptions,
    toastParent?: HTMLElement
): Toast => {
    const newToast = createToast(options);

    (
        toastParent ??
        document.querySelector(".toasts") ??
        document.body
    ).appendChild(newToast);

    const dismissButton = newToast.querySelector(".toast__dismiss-button");

    dismissButton?.addEventListener("click", () => {
        dismissToast(newToast);
    });

    setTimeout(() => {
        displayToast(newToast);
    }, 200);

    if (options.displayDuration && options.displayDuration > 0) {
        setTimeout(() => {
            dismissToast(newToast);
        }, options.displayDuration);
    }
    return newToast;
};

/**
 * Destroy an existing toast element
 *
 * @param toast The toast to dismiss
 */
export function dismissToast(toast: Toast) {
    toast?.classList.remove("toast--visible");
    toast?.removeAttribute("role");
    toast?.setAttribute("aria-hidden", "true");
    setTimeout(() => {
        toast?.parentElement?.removeChild(toast);
    }, 100);
}

/**
 * Make an existing toast element visible
 *
 * @param toast The toast to display
 */
function displayToast(toast: Toast) {
    toast?.removeAttribute("aria-hidden");
    toast?.classList.add("toast--visible");
    toast?.setAttribute("role", "alert");
}

/**
 * Create a new toast element with custom content
 *
 * @param {string} title
 * @param {string} body
 * @param {ToastLevel} level
 * @returns {Toast}
 */
function createToast(options: ToastContent): Toast {
    const toast = document.createElement("div");
    toast.className = `toast toast--${options.level}`;
    toast.setAttribute("aria-hidden", "true");
    toast.innerHTML = /*html*/ `
        <div class="toast__title">
            <span></span>
            <button class="toast__dismiss-button"  aria-label="Dismiss toast">
                <i class="material-icons" aria-hidden="true">close</i>
            </button>
        </div>
        <p class="toast__body">
        </p>
    `;
    const titleSpan = toast.querySelector<HTMLSpanElement>(".toast__title > span")!;
    titleSpan.innerText = options.title;
    const bodyElement = toast.querySelector<HTMLParagraphElement>(".toast__body")!;
    bodyElement.innerText = options.body;

    return toast;
}
